/**
 * Slide-in notes panel for a CMM order row: writes to and reads from Firestore
 * `cmm_workflow/{orderId}/notes_and_history`.
 */
import { Button } from "risa-oasis-ui_v2";
import SideModal from "../../../components/modals/filterModal/filterModal";
import { CmmOrdersTableDataModel } from "../../../data-model/tablesData";
import { CrossIcon } from "../../../svg/cross-icon";
import { useCmmOrderNotesSidebar } from "../hooks/useCmmOrderNotesSidebar";
import { getNoteTimestampLabel } from "../utils/cmmOrderNotesDisplayUtils";
import "./cmmOrderNotesSidebar.scss";

export interface CmmOrderNotesSidebarProps {
  /** When true, the panel is visible. */
  isOpen: boolean;
  /** Called when the user dismisses the panel. */
  onClose: () => void;
  /** Table row; `id` is the `cmm_workflow` document id for the notes subcollection. */
  row: CmmOrdersTableDataModel | null;
  /**
   * Signed-in user label stored as Firestore `poc` (profile name when available,
   * otherwise email / id — see `getLoggedInUserDisplayNameForNotes`).
   */
  loggedInUserName?: string | null;
}

/**
 * Right-side drawer: shows the order's read-only client note, a composer that writes
 * `notes`/`poc`/`timestamp` to Firestore, and a list of previous notes.
 *
 * @param props - Visibility, close handler, selected row, and logged-in user label for `poc`.
 * @returns Right-aligned `SideModal` with header and body.
 */
const CmmOrderNotesSidebar = (props: CmmOrderNotesSidebarProps) => {
  const { isOpen, onClose, row, loggedInUserName } = props;
  const {
    draftNote,
    setDraftNote,
    clientNote,
    notes,
    loading,
    saving,
    error,
    workflowDocId,
    charCount,
    canSave,
    handleSave,
    maxNoteChars,
  } = useCmmOrderNotesSidebar({
    isOpen,
    row,
    loggedInUserName,
  });

  const now = new Date();

  return (
    <SideModal isOpen={isOpen} onClose={onClose} id="cmm-order-notes-sidebar">
      <div className="flex h-full flex-col bg-white">
        <div className="border-b-solid flex items-center justify-between border-b-[0.0625rem] border-b-primaryGray-15 px-[1.25rem] py-[1rem]">
          <div className="m-0 text-lg font-bold text-black">Notes</div>
          <Button
            buttonType="tertiary"
            size="small"
            disabled={false}
            onClick={onClose}
            aria-label="Close notes"
          >
            <CrossIcon fillColor="#374151" />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-[1.25rem] py-[1rem]">
          {row ? (
            <>
              {!workflowDocId ? (
                <div className="mt-0 text-sm text-primaryGray-5">
                  This row has no order id, so notes cannot be saved.
                </div>
              ) : (
                <>
                  {error ? (
                    <div
                      className="mb-[0.75rem] mt-0 rounded-md bg-red-50 px-[0.75rem] py-[0.5rem] text-sm text-red-800"
                      role="alert"
                    >
                      {error}
                    </div>
                  ) : null}

                  {clientNote ? (
                    <div className="mb-[1rem]">
                      <div className="mb-[0.5rem] text-xs font-medium text-primaryGray-5">
                        Client Note
                      </div>
                      <div
                        className="whitespace-pre-wrap rounded-md border-[0.0625rem] border-solid border-primaryGray-15 bg-primaryGray-16 px-[0.75rem] py-[0.5rem] text-sm text-primaryGray-3"
                        aria-readonly="true"
                      >
                        {clientNote}
                      </div>
                    </div>
                  ) : null}

                  <label
                    htmlFor="cmm-order-new-note"
                    className="mb-[0.5rem] block text-xs font-medium text-primaryGray-5"
                  >
                    Add New Note
                  </label>
                  <textarea
                    id="cmm-order-new-note"
                    className="box-border min-h-[7.5rem] w-full resize-y rounded-md border-[0.0625rem] border-solid border-primaryGray-15 px-[0.75rem] py-[0.5rem] text-sm text-black placeholder:text-primaryGray-12 focus:border-[#0056d6] focus:outline-none focus:ring-[0.0625rem] focus:ring-[#0056d6]"
                    placeholder="Enter your comments here..."
                    value={draftNote}
                    maxLength={maxNoteChars}
                    disabled={saving}
                    onChange={(e) => setDraftNote(e.target.value)}
                  />
                  <div className="mt-[0.5rem] flex items-center justify-between gap-[0.75rem]">
                    <span className="text-xs text-primaryGray-5">
                      {charCount} / {maxNoteChars} characters
                    </span>
                    <Button
                      buttonType="primary"
                      size="medium"
                      disabled={!canSave}
                      onClick={() => void handleSave()}
                    >
                      {saving ? "Saving…" : "Save Notes"}
                    </Button>
                  </div>

                  <div className="mb-[0.75rem] mt-[2rem] text-base font-bold text-black">
                    Previous Notes
                  </div>
                  {loading ? (
                    <div className="m-0 text-sm text-primaryGray-5">
                      Loading notes…
                    </div>
                  ) : notes.length === 0 ? (
                    <div className="m-0 text-sm text-primaryGray-5">
                      No notes yet. Add one above.
                    </div>
                  ) : (
                    <ul className="m-0 flex list-none flex-col gap-[0.75rem] p-0">
                      {notes.map((note) => {
                        const when = getNoteTimestampLabel(note.timestamp, now);
                        return (
                          <li
                            key={note.id ?? `${note.poc}-${note.timestamp}`}
                            className="rounded-lg border-[0.0625rem] border-solid border-primaryGray-15 bg-[#F9FAFB] p-[0.75rem]"
                          >
                            <div className="mb-[0.5rem] flex items-start justify-between gap-[0.5rem]">
                              <span className="pl-[0.5rem] text-sm font-bold text-black">
                                {note.poc?.trim() ? note.poc : "—"}
                              </span>
                              <span className="shrink-0 text-xs text-[#6B7280]">
                                {when}
                              </span>
                            </div>
                            <div className="m-0 whitespace-pre-wrap p-[0.5rem] text-sm text-[#374151]">
                              {note.notes}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="text-sm text-primaryGray-5">
              Select a row to view notes.
            </div>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default CmmOrderNotesSidebar;
